// Экран «Предшественники» (вкладка «Связи») — четвёртый экран нового фронта
// (п.20, 2026-09-07). Схема: блоки работ и стрелки между ними; блок таскается мышью,
// от кружка справа тянется новая связь, плюсик добавляет последователя, карандаш —
// карточка работы, клик по подписи стрелки — тип/задержка/сдвиг этажей/мягкая.
// В обычном проекте — один набор «Базовый» без понятия шаблонов; в проекте
// «Глобально» — библиотека шаблонов (свод, шаблон, черновик) и список связей.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ask } from '../dialog';
import { call, p, type Data } from '../api';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';

type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type TplRow = Data<'/api/link-templates/{tid}/rows', 'get'>[number];
type SvodRow = Data<'/api/link-templates/rows', 'get'>[number];
type Tpl = Data<'/api/link-templates', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];

type Edge = {
  id: string | number; work: string; pred: string; tip: string;
  zapas?: number | null; operezhenie?: number | null; soft?: boolean;
  kom?: string;
  tpl?: string | null; foreign?: boolean;
};
type Node = { id: string; name: string; ghost?: boolean; dim?: boolean; tpl?: string | null };
type Mode = 'svod' | 'tpl' | 'draft';
type Draft = { name: string; links: Edge[]; extra: string[]; seq: number };
type Pos = Record<string, { x: number; y: number }>;

const NW = 200;
const POS_KEY = 'plot_gpos';
function readPos(): Pos {
  try { return JSON.parse(localStorage.getItem(POS_KEY) || '{}') as Pos; } catch { return {}; }
}
function savePos(pos: Pos) {
  try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* приватный режим */ }
}
/** Раскладка по уровням: предшественник левее, одинаковый уровень — столбиком */
function layout(nodes: Node[], edges: Edge[], pos: Pos) {
  const lvl: Record<string, number> = {};
  nodes.forEach(n => { lvl[n.id] = 0; });
  for (let k = 0; k < nodes.length; k++) {
    let changed = false;
    edges.forEach(e => {
      const cand = (lvl[e.pred] ?? 0) + 1;
      if (lvl[e.work] != null && cand > lvl[e.work]) { lvl[e.work] = cand; changed = true; }
    });
    if (!changed) break;
  }
  const cols: Record<number, number> = {};
  nodes.slice().sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)).forEach(n => {
    const L = lvl[n.id] || 0;
    cols[L] = cols[L] || 0;
    if (!pos[n.id]) pos[n.id] = { x: 40 + L * 270, y: 26 + cols[L] * 80 };
    cols[L]++;
  });
}
function edgeText(e: Edge, mode: Mode): string {
  let t = e.tip || 'ОН';
  if (e.zapas) t += ' +' + e.zapas + ' дн';
  if (e.operezhenie) t += ' ↑' + e.operezhenie + ' эт';
  if (e.soft) t += ' · мягкая';
  if (e.kom) t += ' 📝';
  if (e.foreign) return 'из «' + e.tpl + '» · ' + t;
  if (mode === 'svod' && e.tpl) t += ' · ' + e.tpl;
  return t;
}

export function Links({ ctx }: { ctx: ShellCtx }) {
  const [works, setWorks] = useState<Work[]>([]);
  const [tpls, setTpls] = useState<Tpl[]>([]);
  const [tplId, setTplId] = useState(0);
  const [rows, setRows] = useState<TplRow[]>([]);
  const [svod, setSvod] = useState<SvodRow[]>([]);
  const [extra, setExtra] = useState<string[]>([]);   // работы, добавленные на схему вручную
  const [draft, setDraft] = useState<Draft | null>(null);
  const [mode, setMode] = useState<Mode>('svod');
  const [isGlobal, setIsGlobal] = useState(false);
  const [msg, setMsg] = useState('');
  const [pop, setPop] = useState<
    | { kind: 'add' | 'copy'; at: Anchor }
    | { kind: 'node' | 'plus'; name: string; at: Anchor }
    | { kind: 'edge'; e: Edge; at: Anchor }
    | null>(null);
  const [, bump] = useState(0);
  const pos = useRef<Pos>(readPos());
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const can = ctx.canPlan;

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  const saved = () => { ctx.onSaved?.(); setMsg(''); };

  const loadRows = useCallback(async (tid: number) => {
    if (!tid) { setRows([]); return; }
    const r = await call<TplRow[]>('get', p('/api/link-templates/{tid}/rows', { tid }));
    if (!r.ok) { setMsg(r.error.message); return; }
    setRows(r.data);
  }, []);

  const load = useCallback(async () => {
    const [w, t, s, pr] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid),
      call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid),
      call<Project[]>('get', '/api/projects'),
    ]);
    if (w.ok) setWorks(w.data);
    if (s.ok) setSvod(s.data);
    let list = t.ok ? t.data : [];
    const gp = pr.ok ? pr.data.find(x => x.code === 'global') : undefined;
    const glob = !!gp && gp.id === ctx.pid;
    setIsGlobal(glob);
    if (!glob) {
      // обычный проект: один набор предшественников — шаблон «Базовый», всегда правка
      let base = list.find(x => x.name.toLowerCase() === 'базовый');
      if (!base && can) {
        const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: ctx.pid });
        if (c.ok) {
          const t2 = await call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid);
          if (t2.ok) { list = t2.data; base = list.find(x => x.id === c.data.id); }
        }
      }
      setTpls(list);
      if (base) { setTplId(base.id); setMode('tpl'); await loadRows(base.id); }
      return;
    }
    setTpls(list);
    if (mode === 'tpl' && tplId) await loadRows(tplId);
  }, [ctx.pid, can, loadRows, mode, tplId]);
  // перечитывать при смене проекта; смена шаблона обрабатывается отдельно
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ctx.pid]);

  // перечитать связи и шаблоны (без пересоздания «Базового»)
  const perechitat = useCallback(async () => {
    const s = await call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid);
    if (s.ok) setSvod(s.data);
    const t = await call<Tpl[]>('get', '/api/link-templates?project=' + ctx.pid);
    if (t.ok) setTpls(t.data);
    if (mode === 'tpl') await loadRows(tplId);
  }, [ctx.pid, mode, tplId, loadRows]);
  // reloadToken — сигнал оболочки «данные изменились» (связь добавлена из карточки
  // работы); без него схема не видела её до перезагрузки страницы (#14, 2026-09-12)
  const pervyjToken = useRef(ctx.reloadToken);
  useEffect(() => {
    if (ctx.reloadToken === pervyjToken.current) return;   // первый рендер уже загрузил load()
    void perechitat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.reloadToken]);
  // своя правка: оболочка по onChanged сама даст новый reloadToken — второй раз не перечитываем
  const reload = async () => { if (ctx.onChanged) ctx.onChanged(); else await perechitat(); };

  // ---- что показываем: свои узлы/связи режима + чужие связи пунктиром ----
  const ownEdges: Edge[] = useMemo(() => {
    if (mode === 'draft') return draft ? draft.links : [];
    if (mode === 'tpl') return rows.map(r => ({ id: r.id, work: r.work, pred: r.pred, tip: r.tip,
      zapas: r.zapas, operezhenie: r.operezhenie, soft: !!r.soft, kom: r.kom || '' }));
    return svod.map((e, i) => ({ id: 'sv' + i, work: e.work, pred: e.pred, tip: e.tip,
      zapas: e.zapas, operezhenie: e.oper, tpl: e.tpl, soft: !!e.soft, kom: e.kom || '' }));
  }, [mode, draft, rows, svod]);

  const ownNodes: Node[] = useMemo(() => {
    const names = new Set<string>(mode === 'draft' ? (draft ? draft.extra : []) : mode === 'tpl' ? extra : []);
    if (mode === 'svod') svod.forEach(e => { names.add(e.work); names.add(e.pred); });
    else ownEdges.forEach(e => { names.add(e.work); names.add(e.pred); });
    return [...names].sort().map(n => ({ id: n, name: n }));
  }, [mode, draft, extra, svod, ownEdges]);

  const tplName = tpls.find(t => t.id === tplId)?.name || '';
  const foreign: Edge[] = useMemo(() => {
    if (mode === 'svod') return [];
    const own = mode === 'tpl' ? tplName : null;
    const onNames = new Set(ownNodes.map(n => n.name));
    const ownPairs = new Set(ownEdges.map(e => e.work + '¶' + e.pred));
    const seen = new Set<string>();
    return svod.filter(e => {
      if (own !== null && e.tpl === own) return false;
      if (!onNames.has(e.work)) return false;
      const k = e.work + '¶' + e.pred;
      if (ownPairs.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    }).map((e, i) => ({ id: 'f' + i, work: e.work, pred: e.pred, tip: e.tip, zapas: e.zapas,
      operezhenie: e.oper, soft: !!e.soft, kom: e.kom || '', tpl: e.tpl, foreign: true }));
  }, [mode, tplName, ownNodes, ownEdges, svod]);

  const view = useMemo(() => {
    const nodes: Node[] = [...ownNodes];
    if (mode !== 'svod') {
      const have = new Set(nodes.map(n => n.name));
      foreign.forEach(e => {
        if (!have.has(e.pred)) { have.add(e.pred); nodes.push({ id: e.pred, name: e.pred, ghost: true, tpl: e.tpl }); }
      });
    }
    const edges = mode === 'svod' ? ownEdges : ownEdges.concat(foreign);
    if (!ctx.filterActive || !ctx.matchesWork) return { nodes, edges };
    const focus = new Set(nodes.filter(n => ctx.matchesWork!(n.name)).map(n => n.id));
    const show = new Set(focus);
    edges.forEach(e => { if (focus.has(e.work) || focus.has(e.pred)) { show.add(e.work); show.add(e.pred); } });
    return {
      nodes: nodes.filter(n => show.has(n.id)).map(n => (focus.has(n.id) ? n : { ...n, dim: true })),
      edges: edges.filter(e => show.has(e.work) && show.has(e.pred)),
    };
  }, [ownNodes, ownEdges, foreign, mode, ctx]);
  layout(view.nodes, view.edges, pos.current);

  // ---- перетаскивание блоков и вытягивание связи от кружка ----
  const drag = useRef<{ id: string; dx: number; dy: number } | null>(null);
  const linkFrom = useRef<string | null>(null);
  const [temp, setTemp] = useState('');
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const xy = (ev: { clientX: number; clientY: number }) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };
  const anchor = (id: string, side: 'in' | 'out') => {
    const q = pos.current[id] || { x: 0, y: 0 };
    const el = canvas.current?.querySelector<HTMLElement>('.g-node[data-id="' + CSS.escape(id) + '"]');
    const h = el ? el.offsetHeight : 46;
    return side === 'out' ? { x: q.x + NW, y: q.y + h / 2 } : { x: q.x, y: q.y + h / 2 };
  };
  useEffect(() => {
    const move = (ev: MouseEvent) => {
      if (drag.current) {
        const c = xy(ev), d = drag.current;
        pos.current[d.id] = { x: Math.max(0, c.x - d.dx), y: Math.max(0, c.y - d.dy) };
        bump(v => v + 1);
      } else if (linkFrom.current) {
        const a = anchor(linkFrom.current, 'out'), c = xy(ev);
        setTemp('M ' + a.x + ' ' + a.y + ' C ' + (a.x + 50) + ' ' + a.y + ', ' + (c.x - 50) + ' ' + c.y + ', ' + c.x + ' ' + c.y);
        const over = (ev.target as HTMLElement).closest('.g-node') as HTMLElement | null;
        setDropTarget(over && over.dataset.id !== linkFrom.current ? over.dataset.id! : null);
      }
    };
    const up = (ev: MouseEvent) => {
      if (drag.current) { savePos(pos.current); drag.current = null; return; }
      if (linkFrom.current) {
        const from = linkFrom.current;
        linkFrom.current = null; setTemp(''); setDropTarget(null);
        const target = (ev.target as HTMLElement).closest('.g-node') as HTMLElement | null;
        if (target && target.dataset.id !== from) void createLink(target.dataset.id!, from);
      }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  function nodeDown(ev: React.MouseEvent, id: string, ghost?: boolean) {
    const t = ev.target as HTMLElement;
    if (t.closest('.gplus') || t.closest('.gedit')) return;
    if (t.closest('.port')) { linkFrom.current = id; ev.preventDefault(); return; }
    if (ghost && mode !== 'svod') { /* призрак тоже таскаем */ }
    const c = xy(ev), q = pos.current[id];
    drag.current = { id, dx: c.x - q.x, dy: c.y - q.y };
    ev.preventDefault();
  }

  // ---- правки связей ----
  async function createLink(work: string, pred: string) {
    if (mode === 'svod' || !can) return;
    if (mode === 'draft' && draft) {
      if (draft.links.some(e => e.work === work && e.pred === pred)) return;
      setDraft({ ...draft, seq: draft.seq + 1,
        links: [...draft.links, { id: 'd' + draft.seq, work, pred, tip: 'ОН', zapas: 0, operezhenie: 0, soft: false }] });
      setMsg('Черновик «' + draft.name + '»: связей ' + (draft.links.length + 1) + ' — не забудьте «Сохранить шаблон».');
      return;
    }
    const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), { work, pred });
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function saveEdge(e: Edge, v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) {
    setPop(null);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, links: draft.links.map(x => (x.id === e.id
        ? { ...x, tip: v.tip, zapas: +v.zapas.replace(',', '.') || 0, operezhenie: +v.oper.replace(',', '.') || 0,
            soft: v.soft, kom: v.kom }
        : x)) });
      return;
    }
    const r = await call('patch', p('/api/template-rows/{rid}', { rid: e.id as number }),
      { tip: v.tip, zapas: v.zapas || 0, operezhenie_etazhej: v.oper || 0, soft: v.soft, kom: v.kom });
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function delEdge(e: Edge) {
    setPop(null);
    if (mode === 'draft' && draft) { setDraft({ ...draft, links: draft.links.filter(x => x.id !== e.id) }); return; }
    const r = await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
    if (!r.ok) { fail(r.error.message); return; }
    saved(); await reload();
  }
  async function swapNode(name: string, nw: Work) {
    setPop(null);
    const mine = ownEdges.filter(e => e.work === name || e.pred === name);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, extra: draft.extra.map(x => (x === name ? nw.name : x)),
        links: draft.links.map(e => ({ ...e, work: e.work === name ? nw.name : e.work, pred: e.pred === name ? nw.name : e.pred })) });
      return;
    }
    for (const e of mine) {
      await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
      const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), {
        work: e.work === name ? nw.name : e.work, pred: e.pred === name ? nw.name : e.pred,
        tip: e.tip || 'ОН', zapas: e.zapas || 0, operezhenie: e.operezhenie || 0, kom: e.kom || '' });
      if (!r.ok) fail(r.error.message);
    }
    setExtra(x => (mine.length ? x.filter(n => n !== name) : x.filter(n => n !== name).concat(nw.name)));
    saved(); await reload();
  }
  async function removeNode(name: string) {
    setPop(null);
    const mine = ownEdges.filter(e => e.work === name || e.pred === name);
    if (mode === 'draft' && draft) {
      setDraft({ ...draft, extra: draft.extra.filter(x => x !== name),
        links: draft.links.filter(e => e.work !== name && e.pred !== name) });
      return;
    }
    if (mine.length && !confirm('У работы ' + mine.length + ' связ. в шаблоне — удалить их?')) return;
    for (const e of mine) await call('delete', p('/api/template-rows/{rid}', { rid: e.id as number }));
    setExtra(x => x.filter(n => n !== name));
    saved(); await reload();
  }
  function addToBoard(name: string) {
    if (mode === 'draft' && draft) setDraft({ ...draft, extra: [...draft.extra, name] });
    else setExtra(x => (x.includes(name) ? x : [...x, name]));
  }

  // ---- шаблоны (только проект «Глобально») ----
  async function pickTpl(v: string) {
    if (v === 'new') {
      const name = (await ask('Название нового шаблона (например «Фасад НВФ»):') || '').trim();
      if (!name) return;
      setDraft({ name, links: [], extra: [], seq: 1 }); setMode('draft');
      setMsg('Черновик «' + name + '»: добавьте работы плюсиком в углу схемы, нарисуйте связи и нажмите «Сохранить шаблон».');
      return;
    }
    if (v) {
      const tid = +v;
      setTplId(tid); setExtra([]); setMode('tpl'); setDraft(null);
      await loadRows(tid);
      setMsg('Правки шаблона сразу меняют связи графика — после изменений нажмите «Актуализировать» на «Модели».');
    } else { setMode('svod'); setDraft(null); setMsg(''); }
  }
  async function saveDraft() {
    if (!draft) return;
    if (!draft.links.length) { setMsg('В черновике пока нет связей.'); return; }
    const r = await call<{ id: number; svyazey: number }>('post', '/api/link-templates', {
      name: draft.name, project: ctx.pid,
      links: draft.links.map(e => ({ work: e.work, pred: e.pred, tip: e.tip, zapas: e.zapas || 0,
        operezhenie: e.operezhenie || 0, soft: !!e.soft, kom: e.kom || '' })) });
    if (!r.ok) { fail(r.error.message); return; }
    setDraft(null);
    await pickTpl(String(r.data.id));
    await reload();
    setMsg('Шаблон сохранён (' + r.data.svyazey + ' св.) и уже действует на график — нажмите «Актуализировать» на «Модели».');
  }
  async function delTpl() {
    if (!tplId) return;
    if (!confirm('Удалить шаблон «' + tplName + '»? Его связи уйдут из графика проекта.')) return;
    const r = await call('delete', p('/api/link-templates/{tid}', { tid: tplId }));
    if (!r.ok) { fail(r.error.message); return; }
    setTplId(0); setMode('svod');
    await reload();
    setMsg('Шаблон удалён — его связи больше не действуют. Нажмите «Актуализировать».');
  }

  const editable = mode !== 'svod' && can;
  return (
    <div>
      <div className="bar actbar">
        {isGlobal && <>
          <label>Шаблон связей{' '}
            <select style={{ minWidth: 240 }} value={mode === 'draft' ? '' : String(tplId || '')} onChange={e => void pickTpl(e.target.value)}>
              <option value="">Все связи графика</option>
              {tpls.map(t => <option key={t.id} value={t.id}>{t.name + ' (' + t.svyazey + ' св.)'}</option>)}
              {can && <option value="new">+ Новый шаблон…</option>}
            </select></label>
          {mode === 'tpl' && can && <button className="del" onClick={() => void delTpl()}>Удалить шаблон</button>}
          {mode === 'draft' && <button className="sec" onClick={() => void saveDraft()}>Сохранить шаблон «{draft?.name}»</button>}
          {can && <button className="sec" title="Скопировать шаблон из другого проекта"
            onClick={e => setPop({ kind: 'copy', at: at(e) })}>Из другого проекта…</button>}
        </>}
        <span className="pw-muted">{msg}</span>
        <span className="grow" />
        <span className="pw-muted">Схема предшественников проекта — то же, что теги в «Работах». Плюсик в углу — добавить работу
          на схему; потяните от кружка блока к следующей работе — появится связь; клик по подписи стрелки — тип, задержка,
          сдвиг этажей. Фильтры сверху оставляют выбранные работы и их соседей (полупрозрачно).</span>
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 10, top: 10, zIndex: 5, display: 'flex', gap: 6 }}>
          {editable && <button title="Добавить работу на схему" onClick={e => setPop({ kind: 'add', at: at(e) })}>＋</button>}
          <button className="sec" title="Сбросить раскладку блоков"
            onClick={() => { pos.current = {}; savePos({}); bump(v => v + 1); }}>⟲</button>
        </div>
        <div ref={wrap} style={{ position: 'relative', height: '72vh', overflow: 'auto',
          border: '1px solid var(--plot-line)', borderRadius: 8, background: '#fbfcfe' }}>
          <div ref={canvas} style={{ position: 'relative', width: 2600, height: 2200 }}>
            <svg width={2600} height={2200} style={{ position: 'absolute', left: 0, top: 0 }}>
              <defs>
                <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
                </marker>
              </defs>
              <g>{view.edges.map(e => {
                const a = anchor(e.pred, 'out'), b = anchor(e.work, 'in');
                const dx = Math.max(40, Math.abs(b.x - a.x) / 2);
                const txt = edgeText(e, mode);
                const wpx = txt.length * 6.5 + 14;
                const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2 - 4;
                return (
                  <g key={String(e.id)}>
                    <path d={'M ' + a.x + ' ' + a.y + ' C ' + (a.x + dx) + ' ' + a.y + ', ' + (b.x - dx) + ' ' + b.y + ', ' + b.x + ' ' + b.y}
                      stroke={e.foreign ? '#98a2b3' : e.soft ? '#f79009' : e.tip === 'НН' ? '#7c3aed' : '#64748b'}
                      strokeWidth={e.foreign ? 1.4 : 1.8} fill="none" markerEnd="url(#arr)"
                      strokeDasharray={e.foreign ? '6 4' : e.soft ? '5 5' : undefined} opacity={e.foreign ? 0.7 : undefined} />
                    <g className={'e-lbl' + (e.foreign ? ' foreign' : '')}
                      onClick={ev => { if (editable && !e.foreign) setPop({ kind: 'edge', e, at: at(ev) }); }}>
                      <rect x={mx - wpx / 2} y={my - 10} width={wpx} height={18} rx={7} />
                      <text x={mx} y={my + 3} textAnchor="middle">{txt}</text>
                    </g>
                  </g>);
              })}</g>
              {temp && <path d={temp} stroke="#1a56db" strokeWidth={2} fill="none" strokeDasharray="5 4" />}
            </svg>
            {view.nodes.map(n => (
              <div key={n.id} className={'g-node' + (n.ghost ? ' ghost' : '') + (n.dim ? ' dim' : '') + (dropTarget === n.id ? ' droptarget' : '')}
                data-id={n.id} style={{ left: pos.current[n.id].x, top: pos.current[n.id].y }}
                title={n.ghost ? 'Предшественник назначен в шаблоне «' + n.tpl + '» — здесь дублировать не нужно'
                  : n.dim ? 'сосед выбранных работ (вне фильтра)'
                  : 'Двойной клик — карточка работы; тяните за кружок — связь'}
                onMouseDown={ev => nodeDown(ev, n.id, n.ghost)}
                onDoubleClick={() => { if (!n.ghost) ctx.onWorkCard?.(n.name); }}>
                {n.name}
                {editable && !n.ghost && <>
                  <div className="port" title="потяните к следующей работе" />
                  <div className="gplus" title="добавить последователя"
                    onClick={ev => setPop({ kind: 'plus', name: n.name, at: at(ev) })}>+</div>
                  <div className="gedit" title="карточка работы: заменить или убрать со схемы"
                    onClick={ev => setPop({ kind: 'node', name: n.name, at: at(ev) })}>✎</div>
                </>}
              </div>
            ))}
            {!view.nodes.length && <div className="g-empty">{ctx.filterActive
              ? 'Под фильтр сверху не попала ни одна работа со связями — измените фильтр или нажмите «Сбросить».'
              : mode === 'svod' ? 'В шаблонах проекта пока нет связей — выберите или создайте шаблон.'
                : 'Пока нет предшественников — добавьте работу плюсиком в углу схемы и потяните стрелку, или задайте их тегами в «Работах».'}</div>}
          </div>
        </div>
      </div>

      {isGlobal && mode === 'tpl' && <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Предшественники списком</summary>
        <AddLinkForm works={works} can={can} onAdd={async v => {
          const r = await call('post', p('/api/link-templates/{tid}/rows', { tid: tplId }), v);
          if (!r.ok) { fail(r.error.message); return; }
          saved(); await reload();
        }} />
        <div className="tbl" style={{ maxHeight: '40vh' }}><table>
          <thead><tr><th>Работа</th><th>Предшественник</th><th>Тип</th><th>Задержка, дн</th><th>Сдвиг этажей</th>
            <th title="Зачем связь и почему такая задержка">Комментарий</th><th></th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id}>
              <td>{r.work}</td><td>{r.pred}</td><td>{r.tip}</td>
              <td className="num">{r.zapas || ''}</td><td className="num">{r.operezhenie || ''}</td>
              <td className="pw-muted" title={r.kom || ''}>{r.kom || ''}</td>
              <td>{can && <button className="del" onClick={() => void delEdge({ id: r.id, work: r.work, pred: r.pred, tip: r.tip })}>×</button>}</td>
            </tr>))}</tbody>
        </table></div>
        <div className="pw-muted" style={{ marginTop: 8 }}>ОН — начнётся после окончания предшественника; НН — вместе с его началом.
          Замкнутые круги сервис не пропустит.</div>
      </details>}

      {pop?.kind === 'add' && <AddNodePop at={pop.at} works={works} on={new Set(ownNodes.map(n => n.name))}
        onClose={() => setPop(null)} onPick={n => { addToBoard(n); setPop(null); }} />}
      {pop?.kind === 'plus' && <AddNodePop at={pop.at} title={'После «' + pop.name + '»'}
        hint="Выберите работу, которая пойдёт следом (связь ОН — начнётся после окончания; тип и запас меняются кликом по стрелке)."
        works={works.filter(w => w.name !== pop.name)} on={new Set(view.edges.filter(e => e.pred === pop.name).map(e => e.work))}
        onClose={() => setPop(null)} onPick={n => { addToBoard(n); void createLink(n, pop.name); setPop(null); }} />}
      {pop?.kind === 'node' && <NodePop at={pop.at} name={pop.name} works={works} on={new Set(ownNodes.map(n => n.name))}
        links={ownEdges.filter(e => e.work === pop.name || e.pred === pop.name).length}
        onClose={() => setPop(null)} onSwap={w => void swapNode(pop.name, w)} onRemove={() => void removeNode(pop.name)} />}
      {pop?.kind === 'edge' && <EdgePop at={pop.at} e={pop.e} onClose={() => setPop(null)}
        onSave={v => void saveEdge(pop.e, v)} onDelete={() => void delEdge(pop.e)} />}
      {pop?.kind === 'copy' && <CopyTplPop at={pop.at} pid={ctx.pid} onClose={() => setPop(null)} onFail={fail}
        onDone={async id => { setPop(null); await reload(); await pickTpl(String(id));
          setMsg('Шаблон скопирован и действует на график — нажмите «Актуализировать».'); }} />}
    </div>
  );
}

function AddLinkForm({ works, can, onAdd }: { works: Work[]; can: boolean;
  onAdd: (v: { work: string; pred: string; tip: string; zapas: number | string; operezhenie: number | string }) => void }) {
  const [v, setV] = useState({ work: '', pred: '', tip: 'ОН', zapas: '', oper: '' });
  if (!can) return null;
  return (
    <div className="bar" style={{ marginTop: 8 }}>
      <select style={{ maxWidth: 320 }} value={v.work} onChange={e => setV(s => ({ ...s, work: e.target.value }))}>
        <option value="">— работа —</option>{works.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}</select>
      <span>после</span>
      <select style={{ maxWidth: 320 }} value={v.pred} onChange={e => setV(s => ({ ...s, pred: e.target.value }))}>
        <option value="">— предшественник —</option>{works.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}</select>
      <select value={v.tip} onChange={e => setV(s => ({ ...s, tip: e.target.value }))}>
        <option value="ОН">окончание → начало (ОН)</option><option value="НН">начало → начало (НН)</option></select>
      <input placeholder="задержка, дн" size={10} value={v.zapas} onChange={e => setV(s => ({ ...s, zapas: e.target.value }))} />
      <input placeholder="сдвиг этажей" size={10} value={v.oper} onChange={e => setV(s => ({ ...s, oper: e.target.value }))} />
      <button onClick={() => { if (v.work && v.pred) onAdd({ work: v.work, pred: v.pred, tip: v.tip, zapas: v.zapas || 0, operezhenie: v.oper || 0 }); }}>
        Добавить связь</button>
    </div>
  );
}

function AddNodePop({ at: a, works, on, title = 'Работа на схему', hint, onClose, onPick }:
  { at: Anchor; works: Work[]; on: Set<string>; title?: string; hint?: string; onClose: () => void; onPick: (name: string) => void }) {
  const cand = works.filter(w => !on.has(w.name));
  const [v, setV] = useState('');
  return (
    <Pop at={a} title={title} onClose={onClose} width={320}>
      {hint && <div className="pw-muted" style={{ margin: '4px 0 8px' }}>{hint}</div>}
      {cand.length === 0
        ? <div className="pw-muted" style={{ margin: '8px 0' }}>Все работы проекта уже на схеме.</div>
        : <div style={{ margin: '10px 0' }}>
            <select style={{ maxWidth: 290 }} value={v} onChange={e => setV(e.target.value)}>
              <option value="">— выбрать работу —</option>
              {cand.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
            </select></div>}
      <div className="bar" style={{ margin: 0 }}><button onClick={() => { if (v) onPick(v); }}>Добавить</button></div>
    </Pop>
  );
}

function NodePop({ at: a, name, works, on, links, onClose, onSwap, onRemove }:
  { at: Anchor; name: string; works: Work[]; on: Set<string>; links: number; onClose: () => void;
    onSwap: (w: Work) => void; onRemove: () => void }) {
  const [v, setV] = useState('');
  const cand = works.filter(w => !on.has(w.name));
  return (
    <Pop at={a} title={name} onClose={onClose} width={330}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Связей у работы на схеме: {links}. Замена перенесёт их на другую работу.</div>
      <div style={{ margin: '0 0 8px' }}>
        <select style={{ maxWidth: 300 }} value={v} onChange={e => setV(e.target.value)}>
          <option value="">— заменить на работу… —</option>
          {cand.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select></div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => { const w = works.find(x => x.id === +v); if (w) onSwap(w); }}>Заменить</button>
        <button className="del" onClick={onRemove}>Убрать со схемы</button>
      </div>
    </Pop>
  );
}

function EdgePop({ at: a, e, onClose, onSave, onDelete }:
  { at: Anchor; e: Edge; onClose: () => void;
    onSave: (v: { tip: string; zapas: string; oper: string; soft: boolean; kom: string }) => void; onDelete: () => void }) {
  const [v, setV] = useState({ tip: e.tip || 'ОН', zapas: e.zapas ? String(e.zapas) : '',
    oper: e.operezhenie ? String(e.operezhenie) : '', soft: !!e.soft, kom: e.kom || '' });
  return (
    <Pop at={a} title={e.pred + ' → ' + e.work} onClose={onClose} width={340}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Окончание → начало: работа начнётся после окончания предшественника;
        начало → начало: вместе с его началом. Задержка — пауза в днях; сдвиг этажей — предшественник берётся с этажа на N выше.</div>
      <div className="bar" style={{ margin: '0 0 8px' }}>
        <select value={v.tip} onChange={ev => setV(s => ({ ...s, tip: ev.target.value }))}>
          <option value="ОН">окончание → начало (ОН)</option><option value="НН">начало → начало (НН)</option></select>
        <input size={9} placeholder="задержка, дн" title="Задержка после предшественника, дней"
          value={v.zapas} onChange={ev => setV(s => ({ ...s, zapas: ev.target.value }))} />
        <input size={9} placeholder="сдвиг этажей" title="Предшественник с этажа на N выше (минус — ниже)"
          value={v.oper} onChange={ev => setV(s => ({ ...s, oper: ev.target.value }))} />
      </div>
      <label className="pw-muted" style={{ display: 'block', margin: '0 0 8px' }}
        title="Мягкая связь держит, пока предшественник идёт по плану; если он отстаёт, связь отпускает и работа идёт вперёд">
        <input type="checkbox" style={{ width: 'auto' }} checked={v.soft} onChange={ev => setV(s => ({ ...s, soft: ev.target.checked }))} />
        {' '}мягкая связь — можно идти вперёд, если предшественник отстаёт</label>
      <textarea rows={2} style={{ width: '100%', margin: '0 0 8px' }} value={v.kom}
        placeholder="Комментарий: зачем связь, почему такая задержка"
        title="Пояснение для себя и коллег: на расчёт не влияет, видно в подсказке связи и в карточке работы"
        onChange={ev => setV(s => ({ ...s, kom: ev.target.value }))} />
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => onSave(v)}>Сохранить</button>
        <button className="del" onClick={onDelete}>Удалить связь</button>
      </div>
    </Pop>
  );
}

function CopyTplPop({ at: a, pid, onClose, onDone, onFail }:
  { at: Anchor; pid: number; onClose: () => void; onDone: (id: number) => void; onFail: (m: string) => void }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [proj, setProj] = useState('');
  const [list, setList] = useState<Tpl[]>([]);
  const [tid, setTid] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await call<Project[]>('get', '/api/projects');
      if (!r.ok) { onFail(r.error.message); return; }
      const others = r.data.filter(x => x.id !== pid);
      setProjects(others);
      if (others.length) setProj(String(others[0].id));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!proj) return;
    void (async () => {
      const r = await call<Tpl[]>('get', '/api/link-templates?project=' + proj);
      if (r.ok) { setList(r.data); setTid(r.data.length ? String(r.data[0].id) : ''); }
    })();
  }, [proj]);
  async function copy() {
    if (!tid) return;
    const src = list.find(x => String(x.id) === tid);
    const name = (await ask('Название копии в этом проекте:', src?.name || '') || '').trim();
    if (!name) return;
    const r = await call<{ id: number }>('post', '/api/link-templates', { name, project: pid, copy_of: +tid });
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.id);
  }
  return (
    <Pop at={a} title="Шаблон из другого проекта" onClose={onClose} width={330}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        <label>Проект<br />
          <select style={{ maxWidth: 300 }} value={proj} onChange={e => setProj(e.target.value)}>
            {projects.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
        <label>Шаблон<br />
          <select style={{ maxWidth: 300 }} value={tid} onChange={e => setTid(e.target.value)}>
            {list.map(t => <option key={t.id} value={t.id}>{t.name + ' (' + t.svyazey + ' св.)'}</option>)}</select></label>
        <div className="bar" style={{ margin: 0 }}><button onClick={() => void copy()}>Скопировать</button></div>
      </div>
    </Pop>
  );
}
